import * as React from "react";
import { Add16Regular } from "@fluentui/react-icons";
import {
    vorgangService,
    type VorgangPickerItem,
    type VorgangPickerTodo,
} from "../../../services/vorgangService";
import { useDebouncedValue } from "../../../utils/useDebouncedValue";
import { useInfiniteRows } from "../../../hooks/useInfiniteRows";
import {
    aufklappLabel,
    ersetzeKey,
    istAufklappbar,
    istNeuesTodoKey,
    kannMehrLaden,
    leerText,
    metaZeile,
    mitGruppenkoepfen,
    neuesTodoKey,
    NUR_TODO_HINWEIS,
    sichtbareTodos,
    stufe1Hinweis,
    terminZeile,
    todoKey,
    vorgangIdAusNeuKey,
    vorgangKey,
} from "./vorgangPickerListe";
import { legeTodoImVorgangAn } from "./todoImVorgangAnlegen";
import { useAuth } from "../../../context/AuthContext";
import { useResponsive } from "../../../hooks/useResponsive";
import { BereichUmschalter } from "./BereichUmschalter";
import type { ScopeKey } from "../../../utils/vorgangScope";
import { betreffOhneAntwortPraefix } from "../../../utils/betreffPraefix";
import { auswahlAenderung, aenderungZahlen, zielKey, type VorbelegtesZiel, type MehrfachAenderung } from "./vorgangMehrfachAuswahl";
import "../detail/vorgangDetail.css";

/**
 * Ausklapp-Mehrfachauswahl „Vorgänge und To-dos" — herausgelöst aus dem
 * Mail-Picker „Bestehendem Vorgang zuordnen" (PO 01.10.2026, F3: Telefonie
 * nutzt DIESELBE Auswahl, nicht eine nachgebaute).
 *
 * Was sie kann (alles wie vorher im Mail-Picker):
 *  - Rangstufen (Kontakt → eigene/Team → Rest) und Bereich Alle/Team/Eigene
 *    (T-56), Gruppe „Deine Vorgänge" (#1966);
 *  - ein Vorgang MIT To-dos ist kein Ziel — aufklappen, To-dos anhaken;
 *    ohne To-dos ist der Vorgang selbst das Ziel (PO 22.09.2026);
 *  - „Neues To-do…" unter einem aufgeklappten Vorgang mit To-dos (T-34):
 *    angelegt wird erst beim Übernehmen, und zwar VOR dem Zuordnen.
 *
 * Neu für die Telefonie: `vorbelegt` — bestehende Zuordnungen stehen oben
 * angehakt („Aktuell zugeordnet") und in der Liste als „verknüpft".
 * „Übernehmen" meldet, was dazukam und was abgehakt wurde
 * (`onUebernehmen({ hinzu, weg })`). Ohne `vorbelegt` verhält sich die
 * Auswahl exakt wie der Mail-Picker vorher.
 *
 * Neu für den Kalender (User 27.10.2026, „Vorgang verknüpfen“): `einzeln` —
 * ein Termin hängt an GENAU einem Vorgang bzw. To-do. Ein neues Häkchen
 * ersetzt dann das vorige.
 *
 * T-409: Kein „Mehr laden“-Knopf mehr. Die Liste lädt die nächste Seite
 * selbst, sobald man ans Ende scrollt — der Knopf lag klebend über den Zeilen.
 */

export type { VorbelegtesZiel, MehrfachAenderung } from "./vorgangMehrfachAuswahl";

export type VorgangMehrfachPickerProps = {
    /** Erkannter Kontakt — Grundlage der Rangstufe 1. */
    contactId?: number | null;
    /** T-34: als Betreff eines neuen To-dos per Knopf übernehmbar. */
    betreffVorschlag?: string | null;
    /** Beschriftung dieses Knopfs (Mail: „Mail-Betreff übernehmen"). */
    betreffVorschlagLabel?: string;
    /** Bestehende Zuordnungen — angehakt vorbelegt. */
    vorbelegt?: VorbelegtesZiel[];
    /**
     * Übernehmen: Neue To-dos sind dann schon angelegt (als `t:<id>` in
     * `auswahl`). Wirft der Aufrufer, bleibt die Auswahl offen und nennt den
     * Fehler — ein zweiter Klick legt kein To-do doppelt an.
     */
    onUebernehmen: (a: MehrfachAenderung & { auswahl: Map<string, number> }) => Promise<void>;
    onAbbrechen: () => void;
    /** Fehlertext je Schritt (Anlegen eines To-dos / Zuordnen). */
    fehlerText: (error: unknown, art: "anlegen" | "zuordnen") => string;
    /** Beschriftung des Hauptknopfs (Standard: „Zuordnen"/„n Zielen zuordnen"). */
    knopfText?: (z: { anzahl: number; hinzu: number; weg: number }) => string;
    /** Genau ein Ziel: ein neues Häkchen ersetzt das vorige (Termin ↔ Vorgang). */
    einzeln?: boolean;
    suchPlatzhalter?: string;
    /**
     * Eigener Block zwischen Liste und Fußleiste — z. B. der Name eines Dokuments,
     * das mit dem Zuordnen angelegt wird (T-397). Ohne Angabe entfällt er.
     */
    zusatz?: React.ReactNode;
};

/** A11: eine Seite füllt den Dialog, ohne einen Scroll-Sprung zu erzeugen. */
const PAGE_SIZE = 30;

/** Fehlerfarbe wie im Vorgang-Detail (`vgd-saveerr`) — es gibt kein Danger-Token. */
const FEHLER_ROT = "#b3261e";

export const VorgangMehrfachPicker: React.FC<VorgangMehrfachPickerProps> = ({
    contactId,
    betreffVorschlag,
    betreffVorschlagLabel = "Mail-Betreff übernehmen",
    vorbelegt,
    onUebernehmen,
    onAbbrechen,
    fehlerText,
    knopfText,
    einzeln = false,
    zusatz,
    suchPlatzhalter = "Vorgang, Nummer, Kunde oder Todo suchen…",
}) => {
    const { user, teams } = useAuth();
    const { isPhone } = useResponsive();
    // T-56: Alle/Team/Eigene wie auf der Vorgänge-Seite; beim Öffnen „Alle".
    const [bereich, setBereich] = React.useState<ScopeKey>("alle");
    /**
     * T-348: Knopf „Persönliche Vorgänge“ (bis T-409 „Eigene Vorgänge“) — nur die EIGENEN persönlichen
     * Vorgänge (ohne Kontakt), um z. B. eine Mail dort abzulegen. Eigene Achse
     * neben dem Bereich (wie die Art „Persönliche“ im Dashboard): Ist er an,
     * entfällt Alle/Team/Eigene, denn ein persönlicher Vorgang hat genau einen
     * Menschen. Beim Öffnen immer aus, wie „Alle“.
     */
    const [nurPersoenliche, setNurPersoenliche] = React.useState(false);
    const hatTeam = (teams?.length ?? 0) > 0;
    // Ohne „AW:"/„WG:"-Stapel, wie beim Betreff eines neuen Vorgangs (E5/E6).
    const mailBetreffRein = betreffOhneAntwortPraefix(betreffVorschlag) || null;
    /**
     * Aufgeklappte Vorgänge und ihre To-dos (PO 30.08.2026: „wenn der vorgang
     * todos hat dann ausklappbar und dann zu einem oder mehreren todos
     * zuweisen"). Geladen wird erst beim Aufklappen — die Liste zeigt bis zu
     * 30 Vorgänge, deren To-dos alle vorab zu holen wäre ein Vielfaches an
     * Daten für etwas, das man an einer Zeile braucht.
     */
    const [offen, setOffen] = React.useState<Set<number>>(new Set());
    const [todosJeVorgang, setTodosJeVorgang] = React.useState<Record<number, VorgangPickerTodo[]>>({});
    const [ladeTodos, setLadeTodos] = React.useState<Set<number>>(new Set());

    const toggleAufklappen = React.useCallback(
        (vorgangId: number) => {
            setOffen((prev) => {
                const next = new Set(prev);
                if (next.has(vorgangId)) next.delete(vorgangId);
                else next.add(vorgangId);
                return next;
            });
            // Einmal geladen, bleibt geladen: erneutes Aufklappen kostet nichts.
            if (todosJeVorgang[vorgangId]) return;
            setLadeTodos((prev) => new Set(prev).add(vorgangId));
            void vorgangService
                .pickerTodos(vorgangId)
                .then((todos) => setTodosJeVorgang((prev) => ({ ...prev, [vorgangId]: todos })))
                .catch((err) => {
                    // Kein Abbruch der Auswahl: die Zeile bleibt, nur ohne
                    // To-dos — zuweisen kann man den Vorgang trotzdem.
                    console.error("[VorgangMehrfachPicker] To-dos laden fehlgeschlagen", err);
                    setTodosJeVorgang((prev) => ({ ...prev, [vorgangId]: [] }));
                })
                .finally(() =>
                    setLadeTodos((prev) => {
                        const next = new Set(prev);
                        next.delete(vorgangId);
                        return next;
                    }),
                );
        },
        [todosJeVorgang],
    );

    const [query, setQuery] = React.useState("");
    const debounced = useDebouncedValue(query, 300);
    const suchbegriff = debounced.trim();
    const [busy, setBusy] = React.useState(false);
    /** T-34: läuft gerade die Anlage neuer To-dos (Knopf-Text)? */
    const [legeAn, setLegeAn] = React.useState(false);
    /** T-34: Betreff je Vorgang für „Neues To-do…" und offene Eingabefelder. */
    const [neueTodos, setNeueTodos] = React.useState<Map<number, string>>(() => new Map());
    const [neuFeldOffen, setNeuFeldOffen] = React.useState<Set<number>>(() => new Set());
    const [fehler, setFehler] = React.useState<string | null>(null);
    // Auswahl über stabile Schlüssel: die Liste wechselt beim Tippen, und ein
    // Set<number> könnte Vorgang 7 und Todo 7 nicht unterscheiden. Der Wert
    // speichert zugleich den Elternvorgang für die spätere Navigation.
    //
    // Bereits bestehende Zuordnungen (`vorbelegt`) stehen von Anfang an drin;
    // „Übernehmen" vergleicht die Auswahl mit ihnen (hinzu/weg).
    const vorbelegtJeKey = React.useMemo(
        () => new Map((vorbelegt ?? []).map((z) => [zielKey(z), z] as const)),
        [vorbelegt],
    );
    const [vorgangJeZiel, setVorgangJeZiel] = React.useState<Map<string, number>>(
        () => new Map((vorbelegt ?? []).map((z) => [zielKey(z), z.vorgangId] as const)),
    );
    const selected = React.useMemo(() => new Set(vorgangJeZiel.keys()), [vorgangJeZiel]);
    const aenderung = React.useMemo(
        () => auswahlAenderung(vorgangJeZiel, vorbelegtJeKey),
        [vorgangJeZiel, vorbelegtJeKey],
    );
    const hatAenderung = aenderung.hinzu.length + aenderung.weg.length + aenderung.neu > 0;

    const fetchPage = React.useCallback(
        async ({ limit, offset, signal }: { limit: number; offset: number; signal: AbortSignal }) => {
            const { items, total } = await vorgangService.getPickerPage({
                contactId: contactId ?? null,
                q: suchbegriff || undefined,
                // PO 28.09.2026: „keine Zuweisung in Mail zu erledigten
                // Vorgängen anzeigen" — die Liste ohne Suche bleibt bei den
                // offenen. T-272 (09.10.2026): Wer gezielt sucht, findet auch
                // erledigte Vorgänge (stehen hinten, Status „Erledigt" in der
                // Zeile) — sonst ließ sich eine Nachzügler-Mail nicht mehr zum
                // abgeschlossenen Vorgang legen.
                nurOffene: !suchbegriff,
                bereich: nurPersoenliche ? undefined : bereich,
                art: nurPersoenliche ? "persoenlich" : null,
                limit,
                offset,
                signal,
            });
            return { rows: items, total };
        },
        [contactId, suchbegriff, bereich, nurPersoenliche],
    );

    const { rows, total, hasMore, loading, error, loadMore } = useInfiniteRows<VorgangPickerItem>({
        fetchPage,
        getKey: (item) => item.vorgang.id,
        pageSize: PAGE_SIZE,
        deps: [contactId ?? null, suchbegriff, bereich, nurPersoenliche],
    });

    const zeilen = React.useMemo(() => mitGruppenkoepfen(rows), [rows]);

    /**
     * T-409: Nachladen beim Scrollen. Ein unsichtbarer Streifen am Listenende
     * holt die nächste Seite, sobald er in Sichtweite kommt (200 px Vorlauf) —
     * Muster der Anrufliste. Der Beobachter entsteht nach jeder Seite neu: ist
     * die Liste noch zu kurz zum Scrollen, meldet er sich gleich wieder.
     */
    const listeRef = React.useRef<HTMLDivElement | null>(null);
    const endeRef = React.useRef<HTMLDivElement | null>(null);
    const mehrDa = rows.length > 0 && hasMore && kannMehrLaden(rows.length, total);
    React.useEffect(() => {
        const root = listeRef.current;
        const ende = endeRef.current;
        if (!root || !ende || !mehrDa || loading || error) return;
        if (typeof IntersectionObserver === "undefined") return;
        const beobachter = new IntersectionObserver(
            (eintraege) => {
                if (eintraege.some((e) => e.isIntersecting)) loadMore();
            },
            { root, rootMargin: "0px 0px 200px 0px" },
        );
        beobachter.observe(ende);
        return () => beobachter.disconnect();
    }, [mehrDa, loading, error, loadMore, rows.length]);
    // Persönliche Vorgänge haben nie einen Kontakt — der Stufe-1-Hinweis
    // („zu diesem Kontakt gibt es noch keine …“) wäre dort keine Aussage.
    const hinweis = nurPersoenliche ? null : stufe1Hinweis({
        mitSuche: !!suchbegriff,
        mitKontakt: contactId != null,
        items: rows,
    });

    const toggle = (key: string, vorgangId: number) =>
        setVorgangJeZiel((aktuell) => {
            if (aktuell.has(key)) {
                const next = new Map(aktuell);
                next.delete(key);
                return next;
            }
            // `einzeln`: das neue Ziel ersetzt das vorige.
            const next = einzeln ? new Map<string, number>() : new Map(aktuell);
            next.set(key, vorgangId);
            return next;
        });

    /**
     * T-34: Betreff des neuen To-dos setzen. Nicht leer → als Ziel ausgewählt
     * (`n:<vorgangId>`), leer → wieder heraus. So zählen Fußzeile und Knopf
     * das neue To-do automatisch mit.
     */
    const setzeNeuBetreff = (vorgangId: number, text: string) => {
        setNeueTodos((prev) => new Map(prev).set(vorgangId, text));
        setVorgangJeZiel((aktuell) => {
            const key = neuesTodoKey(vorgangId);
            if (!text.trim()) {
                const next = new Map(aktuell);
                next.delete(key);
                return next;
            }
            const next = einzeln && !aktuell.has(key) ? new Map<string, number>() : new Map(aktuell);
            next.set(key, vorgangId);
            return next;
        });
    };

    const neuFeldSchliessen = (vorgangId: number) => {
        setNeuFeldOffen((prev) => {
            const next = new Set(prev);
            next.delete(vorgangId);
            return next;
        });
        setNeueTodos((prev) => {
            const next = new Map(prev);
            next.delete(vorgangId);
            return next;
        });
        setVorgangJeZiel((aktuell) => {
            const next = new Map(aktuell);
            next.delete(neuesTodoKey(vorgangId));
            return next;
        });
    };

    const confirm = async () => {
        if (!hatAenderung) return;
        setBusy(true);
        setFehler(null);
        // Lokale Kopie: der State zieht erst im nächsten Render nach.
        let auswahl: Map<string, number> = new Map(vorgangJeZiel);
        try {
            // T-34: erst die neuen To-dos anlegen. Jedes angelegte steht SOFORT
            // als `t:<id>` in der Auswahl — scheitert danach etwas, legt ein
            // zweiter Klick es nicht noch einmal an.
            for (const key of Array.from(auswahl.keys()).filter(istNeuesTodoKey)) {
                const vorgangId = vorgangIdAusNeuKey(key);
                if (vorgangId == null) continue;
                setLegeAn(true);
                let angelegt: { id: number; titel: string };
                try {
                    angelegt = await legeTodoImVorgangAn({
                        vorgangId,
                        titel: neueTodos.get(vorgangId) ?? "",
                        currentUserId: user?.id ?? null,
                    });
                } catch (error) {
                    setFehler(
                        (error as { status?: number } | null)?.status === 403
                            ? "Kein Recht, in diesem Vorgang ein To-do anzulegen."
                            : fehlerText(error, "anlegen"),
                    );
                    return;
                }
                auswahl = ersetzeKey(auswahl, key, todoKey(angelegt.id));
                setVorgangJeZiel(auswahl);
                setTodosJeVorgang((prev) => ({
                    ...prev,
                    [vorgangId]: [...(prev[vorgangId] ?? []), { id: angelegt.id, titel: angelegt.titel, status: "Offen" }],
                }));
                setNeueTodos((prev) => {
                    const next = new Map(prev);
                    next.delete(vorgangId);
                    return next;
                });
                setNeuFeldOffen((prev) => {
                    const next = new Set(prev);
                    next.delete(vorgangId);
                    return next;
                });
            }
            setLegeAn(false);
            const { hinzu, weg } = auswahlAenderung(auswahl, vorbelegtJeKey);
            await onUebernehmen({ auswahl, hinzu, weg });
        } catch (error) {
            setFehler(fehlerText(error, "zuordnen"));
        } finally {
            setLegeAn(false);
            setBusy(false);
        }
    };

    const ladeFehler = error ? "Vorgänge konnten nicht geladen werden." : null;

    return (
        <div
            className="vgd"
            style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0, background: "#fff" }}
        >
            {/* `kvp-rd`-Hülle, damit die `rbac-seg`-Optik der Vorgänge-Seite greift. */}
            <div className="kvp-rd vgp-bereich-zeile">
                {!nurPersoenliche && (
                    <BereichUmschalter wert={bereich} onChange={setBereich} hatTeam={hatTeam} disabled={busy} />
                )}
                {/* T-348: eigene Leiste, damit er nicht als vierter Bereich
                    neben „Eigene“ gelesen wird. */}
                <div className="rbac-seg vgp-bereich vgp-art" role="group" aria-label="Art">
                    <button
                        type="button"
                        className={"rbac-seg-btn" + (nurPersoenliche ? " on" : "")}
                        aria-pressed={nurPersoenliche}
                        disabled={busy}
                        title={
                            nurPersoenliche
                                ? "Zurück zur normalen Liste"
                                : "Nur deine persönlichen Vorgänge (ohne Kontakt) zeigen"
                        }
                        onClick={() => setNurPersoenliche((an) => !an)}
                    >
                        <span className="rbac-dot" />
                        Persönliche Vorgänge
                    </button>
                </div>
            </div>
            <input
                className="vgd-linkdialog-search"
                // Auf dem Phone öffnete der Autofokus die Tastatur, die dann die
                // untere Hälfte samt „Zuordnen" verdeckte (T-199).
                autoFocus={!isPhone}
                placeholder={suchPlatzhalter}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
            />

            {/* Bestehende Zuordnungen (z. B. eines Anrufs): oben, angehakt —
                Abhaken löst sie beim „Übernehmen". Sie stehen hier, weil der
                Vorgang in der Liste darunter fehlen kann (Bereich, Seite). */}
            {(vorbelegt?.length ?? 0) > 0 && (
                <div className="vgd-picker-vorbelegt">
                    <div className="vgd-picker-group">
                        <span className="vgd-zt-grouptitle">Aktuell zugeordnet</span>
                    </div>
                    {vorbelegt!.map((z) => (
                        <label key={`vb${zielKey(z)}`} className="vgd-linkdialog-row checkbox vgd-picker-row">
                            <input
                                type="checkbox"
                                checked={selected.has(zielKey(z))}
                                disabled={busy}
                                onChange={() => toggle(zielKey(z), z.vorgangId)}
                                aria-label={`Zugeordnet: ${z.titel}`}
                            />
                            <span className="vgd-linkdialog-namecell">
                                <span className="vgd-linkdialog-main" title={z.titel}>
                                    {z.titel}
                                </span>
                                {z.unter && <span className="vgd-linkdialog-sub">{z.unter}</span>}
                            </span>
                        </label>
                    ))}
                </div>
            )}

            {/* Der Hinweis verweist auf die Zeilen „unten" — ohne Zeilen entfällt er. */}
            {hinweis && rows.length > 0 && <div className="vgd-linkdialog-hint">{hinweis}</div>}

            <div ref={listeRef} className="vgd-linkdialog-list" style={{ flex: 1, minHeight: 0 }}>
                {loading && rows.length === 0 ? (
                    <div className="vgd-linkdialog-empty">Laden…</div>
                ) : rows.length === 0 ? (
                    <div className="vgd-linkdialog-empty">
                        {leerText({ mitSuche: !!suchbegriff, mitKontakt: contactId != null, bereich, nurPersoenliche })}
                    </div>
                ) : (
                    zeilen.map((zeile) =>
                        zeile.kind === "gruppe" ? (
                            <div key={`g${zeile.schluessel}`} className="vgd-picker-group">
                                <span className="vgd-zt-grouptitle">{zeile.titel}</span>
                            </div>
                        ) : (
                            <React.Fragment key={zeile.item.vorgang.id}>
                                {/* EIN VORGANG MIT TO-DOS IST KEIN ZIEL
                                    (PO 22.09.2026). Seine Checkbox ist gesperrt;
                                    gewählt wird eines seiner To-dos, und der
                                    Aufklapp-Knopf daneben führt dorthin.

                                    Die Zeile bleibt SICHTBAR und lesbar — sie ist
                                    der Weg zu den To-dos. Sie auszublenden hieße,
                                    den Vorgang unauffindbar zu machen. */}
                                <label
                                    className={
                                        "vgd-linkdialog-row checkbox vgd-picker-row" +
                                        (istAufklappbar(zeile.item) ? " vgd-picker-nurtodo" : "")
                                    }
                                    // T-199: Ein Tipp irgendwo auf die Zeile klappt
                                    // die To-dos auf — vorher landete er auf der
                                    // gesperrten Checkbox und tat nichts. Gewählt
                                    // wird weiterhin nur ein To-do.
                                    onClick={
                                        istAufklappbar(zeile.item)
                                            ? (e) => {
                                                  e.preventDefault();
                                                  toggleAufklappen(zeile.item.vorgang.id);
                                              }
                                            : undefined
                                    }
                                >
                                    <input
                                        type="checkbox"
                                        checked={selected.has(vorgangKey(zeile.item.vorgang.id))}
                                        disabled={istAufklappbar(zeile.item)}
                                        title={
                                            istAufklappbar(zeile.item)
                                                ? "Dieser Vorgang ist in To-dos gegliedert — wähle eines davon."
                                                : undefined
                                        }
                                        onChange={() =>
                                            toggle(
                                                vorgangKey(zeile.item.vorgang.id),
                                                zeile.item.vorgang.id,
                                            )
                                        }
                                        aria-label={zeile.item.vorgang.titel}
                                    />
                                    <span className="vgd-linkdialog-namecell">
                                        <span className="vgd-linkdialog-main" title={zeile.item.vorgang.titel}>
                                            {zeile.item.vorgang.titel}
                                        </span>
                                        <span className="vgd-linkdialog-sub">{metaZeile(zeile.item)}</span>
                                        {istAufklappbar(zeile.item) && (
                                            <span className="vgd-linkdialog-sub vgd-picker-hinweis">{NUR_TODO_HINWEIS}</span>
                                        )}
                                    </span>
                                    {vorbelegtJeKey.has(vorgangKey(zeile.item.vorgang.id)) && (
                                        <span className="vgd-picker-verknuepft">verknüpft</span>
                                    )}
                                    {terminZeile(zeile.item) && (
                                        <span className="vgd-linkdialog-sub">{terminZeile(zeile.item)}</span>
                                    )}
                                    {/* Aufklappen: der Knopf sitzt IM Label und muss
                                        deshalb verhindern, dass der Klick als Klick
                                        auf die Checkbox durchgeht. */}
                                    {istAufklappbar(zeile.item) && (
                                        <button
                                            type="button"
                                            className="vgd-picker-toggle"
                                            aria-expanded={offen.has(zeile.item.vorgang.id)}
                                            title={aufklappLabel(zeile.item, offen.has(zeile.item.vorgang.id))}
                                            aria-label={aufklappLabel(zeile.item, offen.has(zeile.item.vorgang.id))}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                toggleAufklappen(zeile.item.vorgang.id);
                                            }}
                                        >
                                            <span className="vgd-picker-toggle-n">
                                                {zeile.item.vorgang.todoAnzahl}
                                            </span>
                                            {/* Das Wort dazu (PO 22.09.2026: „das
                                                arrow icon ist zu unübersichtlich").
                                                Eine Zahl mit Pfeil allein verrät
                                                nicht, wovon sie handelt. */}
                                            <span className="vgd-picker-toggle-w">
                                                {zeile.item.vorgang.todoAnzahl === 1 ? "To-do" : "To-dos"}
                                            </span>
                                            <span
                                                className={`vgd-picker-chev${offen.has(zeile.item.vorgang.id) ? " offen" : ""}`}
                                                aria-hidden
                                            >
                                                ›
                                            </span>
                                        </button>
                                    )}
                                </label>
                                {ladeTodos.has(zeile.item.vorgang.id) && (
                                    <div className="vgd-linkdialog-row vgd-picker-sub vgd-linkdialog-sub">
                                        To-dos werden geladen…
                                    </div>
                                )}
                                {/* Unterzeilen: bei aktiver Suche die Treffer, nach dem
                                    Aufklappen alle To-dos des Vorgangs. */}
                                {(offen.has(zeile.item.vorgang.id) || zeile.item.todos.length > 0) &&
                                    sichtbareTodos(
                                        zeile.item,
                                        offen.has(zeile.item.vorgang.id)
                                            ? todosJeVorgang[zeile.item.vorgang.id]
                                            : undefined,
                                    ).map((t) => (
                                        <label key={`t${t.id}`} className="vgd-linkdialog-row checkbox vgd-picker-row vgd-picker-sub">
                                            <input
                                                type="checkbox"
                                                checked={selected.has(todoKey(t.id))}
                                                onChange={() =>
                                                    toggle(todoKey(t.id), zeile.item.vorgang.id)
                                                }
                                                aria-label={`Todo ${t.titel}`}
                                            />
                                            <span className="vgd-linkdialog-main">↳ {t.titel}</span>
                                            {t.status && <span className="vgd-linkdialog-sub">{t.status}</span>}
                                            {vorbelegtJeKey.has(todoKey(t.id)) && (
                                                <span className="vgd-picker-verknuepft">verknüpft</span>
                                            )}
                                        </label>
                                    ))}
                                {offen.has(zeile.item.vorgang.id) &&
                                    !ladeTodos.has(zeile.item.vorgang.id) &&
                                    (todosJeVorgang[zeile.item.vorgang.id]?.length ?? 0) === 0 && (
                                        <div className="vgd-linkdialog-row vgd-picker-sub vgd-linkdialog-sub">
                                            Keine To-dos.
                                        </div>
                                    )}
                                {/* T-34: „Neues To-do…" — nur unter einem aufgeklappten
                                    Vorgang MIT To-dos (ohne To-dos ist der Vorgang
                                    selbst das Ziel). Steht auch bei leerer Vollliste
                                    da, damit ein veralteter Zähler einen Ausweg hat. */}
                                {offen.has(zeile.item.vorgang.id) &&
                                    istAufklappbar(zeile.item) &&
                                    !ladeTodos.has(zeile.item.vorgang.id) &&
                                    (neuFeldOffen.has(zeile.item.vorgang.id) ? (
                                        <div className="vgd-linkdialog-row vgd-picker-sub vgd-picker-neu-feld">
                                            <input
                                                className="vgd-linkdialog-search"
                                                autoFocus
                                                aria-label="Betreff des neuen To-dos"
                                                placeholder="Betreff des neuen To-dos"
                                                value={neueTodos.get(zeile.item.vorgang.id) ?? ""}
                                                disabled={busy}
                                                onChange={(e) => setzeNeuBetreff(zeile.item.vorgang.id, e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter") {
                                                        e.preventDefault();
                                                        e.currentTarget.blur();
                                                    } else if (e.key === "Escape") {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        neuFeldSchliessen(zeile.item.vorgang.id);
                                                    }
                                                }}
                                            />
                                            {mailBetreffRein && (
                                                <button
                                                    type="button"
                                                    className="vgd-btn vgd-picker-neu-btn"
                                                    disabled={busy}
                                                    title={`${betreffVorschlagLabel}: ${mailBetreffRein}`}
                                                    onClick={() => setzeNeuBetreff(zeile.item.vorgang.id, mailBetreffRein)}
                                                >
                                                    {betreffVorschlagLabel}
                                                </button>
                                            )}
                                        </div>
                                    ) : (
                                        <button
                                            type="button"
                                            className="vgd-linkdialog-row vgd-picker-sub vgd-picker-neu"
                                            disabled={busy}
                                            onClick={() =>
                                                setNeuFeldOffen((prev) => new Set(prev).add(zeile.item.vorgang.id))
                                            }
                                        >
                                            <Add16Regular aria-hidden /> Neues To-do…
                                        </button>
                                    ))}
                            </React.Fragment>
                        ),
                    )
                )}

                {/* T-409: Streifen fürs Nachladen — nur, wenn tatsächlich etwas
                    darüber steht UND der Server mehr meldet. */}
                {mehrDa && (
                    <div ref={endeRef} className="vgd-linkdialog-empty" role="status" style={{ padding: "10px 8px" }}>
                        {loading ? "Weitere Vorgänge werden geladen…" : ""}
                    </div>
                )}
            </div>

            {zusatz && <div style={{ padding: "10px 14px", borderTop: "1px solid var(--vgd-line)" }}>{zusatz}</div>}

            {(fehler || ladeFehler) && (
                <div style={{ padding: "8px 14px", color: FEHLER_ROT, fontSize: "0.8rem", fontWeight: 700 }}>
                    {fehler ?? ladeFehler}
                </div>
            )}

            <div className="vgd-linkdialog-foot">
                <span className="vgd-linkdialog-count">{selected.size} ausgewählt</span>
                <button type="button" className="vgd-btn" onClick={onAbbrechen} disabled={busy}>
                    Abbrechen
                </button>
                <button
                    type="button"
                    className="vgd-btn primary"
                    disabled={!hatAenderung || busy}
                    onClick={() => void confirm()}
                >
                    {legeAn
                        ? "To-do wird angelegt…"
                        : busy
                          ? "Wird zugeordnet…"
                          : knopfText
                            ? knopfText({ anzahl: selected.size, ...aenderungZahlen(aenderung) })
                            : selected.size > 1 ? `${selected.size} Zielen zuordnen` : "Zuordnen"}
                </button>
            </div>
        </div>
    );
};


export default VorgangMehrfachPicker;
